import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  List,
  Radio,
  Row,
  Segmented,
  Space,
  Table,
  Tag,
  Typography,
  type TableColumnsType,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useProductsGetProduct } from '@/api/generated/endpoints/products/products';
import { useStockReceive } from '@/api/generated/endpoints/stock/stock';
import type { BatchCreate, BatchOut, ProductRef, Uom } from '@/api/generated/model';
import { ApiProblem } from '@/api/http';
import { ProductSelect } from '@/components/ProductSelect';
import { QtyCell, QtyInput } from '@/components/qty';
import { problemMessage } from '@/i18n/problem';
import { applyProblem } from '@/lib/problemForm';
import { qtyRule, type QtyValue } from '@/lib/qty';
import { invalidatePath } from '@/lib/queryCache';
import { blankToNull } from '@/lib/text';
import { useIsMobile } from '@/lib/useIsMobile';
import { useOutbox } from '@/offline/context';
import { isNetworkError } from '@/offline/network';
import { refreshProductCache } from '@/offline/productCache';
import { uomLabel } from '@/lib/units';
import type { OutboxOp } from '@/offline/outbox';
import { LabelPrintModal, type LabelTarget } from '@/pages/stock/LabelPrintModal';

type Mode = 'existing' | 'new';

interface ReceiptForm {
  product_id: number | null;
  new_name?: string;
  new_uom: Uom;
  new_shelf_life?: number | null;
  qty: QtyValue;
  /** Товар в коробках: вес одной коробки этой поставки, кг — необязателен. */
  box_weight?: QtyValue;
  expires_on: Dayjs | null;
  received_on: Dayjs;
  country?: string;
  caliber?: string;
  supplier?: string;
  comment?: string;
  /** ADR-0011: число палетов обязательно, либо явно «Без палетов». */
  pallets: number | null;
  no_pallets: boolean;
}

interface Candidate {
  product: ProductRef;
  score: number;
}

const DATE = 'DD.MM.YYYY';
// Поля одной позиции: после записи сбрасываются. Поставщик и дата — общие для поставки.
const POSITION_FIELDS: (keyof ReceiptForm)[] = [
  'product_id',
  'new_name',
  'new_shelf_life',
  'qty',
  'box_weight',
  'expires_on',
  'country',
  'caliber',
  'comment',
  'pallets',
  'no_pallets',
];

/** Приёмка на склад (ADR-0005): несколько позиций одной поставки подряд. */
export function ReceivingPage() {
  const { t } = useTranslation();
  const [form] = Form.useForm<ReceiptForm>();
  const [mode, setMode] = useState<Mode>('existing');
  const [product, setProduct] = useState<ProductRef | null>(null);
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<BatchOut[]>([]);
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const outbox = useOutbox();
  const formMobile = useIsMobile();

  const detail = useProductsGetProduct(product?.id ?? 0, {
    query: { enabled: mode === 'existing' && product !== null },
  });
  // До первой отрисовки поля useWatch отдаёт undefined, хотя тип этого не говорит.
  const newUom = Form.useWatch('new_uom', form) as Uom | undefined;
  const newShelfLife = Form.useWatch('new_shelf_life', form);
  const expiresOn = Form.useWatch('expires_on', form);
  const receivedOn = Form.useWatch('received_on', form) as Dayjs | undefined;
  const noPallets = (Form.useWatch('no_pallets', form) as boolean | undefined) ?? false;
  const uom: Uom | null = mode === 'new' ? (newUom ?? 'kg') : (product?.base_uom ?? null);
  const shelfLife = mode === 'new' ? newShelfLife : detail.data?.shelf_life_days;
  // Вес коробки прошлой партии этого товара — подсказка: поставщик обычно тот же.
  const lastBoxWeight = mode === 'existing' ? detail.data?.last_box_weight_kg : null;
  useEffect(() => {
    if (lastBoxWeight && !form.getFieldValue('box_weight')) {
      form.setFieldValue('box_weight', lastBoxWeight);
    }
  }, [lastBoxWeight, form]);

  const receive = useStockReceive({
    mutation: {
      onSuccess: async (batch) => {
        setSession((s) => [batch, ...s]);
        void message.success(
          t('receiving.done', {
            product: batch.product.name,
            qty: `${batch.qty_received} ${uomLabel(t, batch.product.base_uom)}`,
          }),
        );
        form.resetFields(POSITION_FIELDS);
        setMode('existing');
        setProduct(null);
        setCandidates(null);
        await Promise.all([
          invalidatePath(queryClient, '/api/v1/batches'),
          invalidatePath(queryClient, '/api/v1/stock'),
          invalidatePath(queryClient, '/api/v1/products'),
          refreshProductCache(), // новый товар — сразу в копию для работы без связи
        ]);
      },
      onError: (e, { data }) => {
        if (isNetworkError(e)) {
          queueReceipt(data);
          return;
        }
        // Похожий товар уже есть — это вопрос «может, это он?», а не ошибка (ADR-0005 п.2).
        if (e instanceof ApiProblem && e.code === 'similar_products') {
          setCandidates((e.extra.candidates as Candidate[] | undefined) ?? []);
          return;
        }
        if (!applyProblem(form, e, { product_exists: 'new_name' })) setError(problemMessage(e));
      },
    },
  });

  /** Нет связи — приёмка ждёт в очереди и уйдёт сама (ADR-0009, T-182). */
  function queueReceipt(data: BatchCreate) {
    outbox.markOffline();
    const name = data.new_product?.name ?? product?.name ?? '';
    const qty = `${data.qty} ${uomLabel(t, uom ?? 'kg')}`;
    outbox.enqueue({
      kind: 'receive',
      url: '/api/v1/batches',
      body: { ...data },
      label: t('offline.receiveLabel', { product: name, qty }),
    });
    void message.warning(t('offline.queuedReceipt', { product: name, qty }));
    form.resetFields(POSITION_FIELDS);
    setMode('existing');
    setProduct(null);
    setCandidates(null);
  }

  function submit(v: ReceiptForm, confirmNew = false) {
    setError(null);
    if (typeof v.qty !== 'string') return; // qtyRule не пропустит
    const today = dayjs().isSame(v.received_on, 'day');
    const data: BatchCreate = {
      qty: v.qty,
      expires_on: v.expires_on ? v.expires_on.format('YYYY-MM-DD') : null,
      // Задним числом — полдень того дня: дата приёмки не «уедет» из-за часового пояса.
      // Без связи — время приёмки фиксируем сейчас: отправка может уйти и завтра.
      received_at: today
        ? outbox.online
          ? null
          : new Date().toISOString()
        : v.received_on.hour(12).minute(0).second(0).toISOString(),
      country: blankToNull(v.country),
      caliber: blankToNull(v.caliber),
      supplier: blankToNull(v.supplier),
      comment: blankToNull(v.comment),
      pallets: v.no_pallets ? null : v.pallets,
      ...(uom === 'box' && typeof v.box_weight === 'string' ? { box_weight_kg: v.box_weight } : {}),
      ...(mode === 'new'
        ? {
            new_product: {
              name: v.new_name ?? '',
              base_uom: v.new_uom,
              shelf_life_days: v.new_shelf_life ?? null,
              confirm_new: confirmNew,
            },
          }
        : { product_id: v.product_id }),
    };
    if (!outbox.online) {
      queueReceipt(data);
      return;
    }
    receive.mutate({ data });
  }

  function expiryHint(): string {
    if (expiresOn) return t('receiving.expiresHint');
    if (shelfLife && receivedOn) {
      return t('receiving.expiresAuto', {
        date: receivedOn.add(shelfLife, 'day').format(DATE),
        days: shelfLife,
      });
    }
    return t('receiving.expiresNone');
  }

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} xl={14}>
        <Card title={t('receiving.title')}>
          <Typography.Paragraph type="secondary">{t('receiving.hint')}</Typography.Paragraph>
          <Form<ReceiptForm>
            form={form}
            layout="vertical"
            initialValues={{
              new_uom: 'kg',
              received_on: dayjs(),
              qty: null,
              expires_on: null,
              pallets: null,
              no_pallets: false,
            }}
            onFinish={(v) => {
              submit(v);
            }}
            onValuesChange={(changed: Partial<ReceiptForm>) => {
              if ('new_name' in changed) setCandidates(null);
            }}
          >
            <Form.Item label={t('receiving.product')} required style={{ marginBottom: 8 }}>
              <Segmented<Mode>
                value={mode}
                options={[
                  { value: 'existing', label: t('receiving.pickExisting') },
                  { value: 'new', label: t('receiving.createNew') },
                ]}
                onChange={(m) => {
                  setMode(m);
                  setCandidates(null);
                  form.setFieldValue('qty', null);
                }}
              />
            </Form.Item>
            {mode === 'existing' ? (
              <Form.Item
                name="product_id"
                rules={[{ required: true, message: t('receiving.productRequired') }]}
              >
                <ProductSelect
                  ariaLabel={t('receiving.product')}
                  onChange={(_, p) => {
                    setProduct(p);
                    form.setFieldValue('qty', null);
                  }}
                />
              </Form.Item>
            ) : (
              <>
                <Form.Item
                  name="new_name"
                  label={t('receiving.newName')}
                  rules={[
                    { required: true, whitespace: true, message: t('products.nameRequired') },
                  ]}
                >
                  <Input maxLength={300} autoFocus />
                </Form.Item>
                <Space size="large" wrap>
                  <Form.Item name="new_uom" label={t('receiving.newUom')}>
                    <Radio.Group
                      optionType="button"
                      options={[
                        { label: t('uom.kgLong'), value: 'kg' },
                        { label: t('uom.pcsLong'), value: 'pcs' },
                        { label: t('uom.boxLong'), value: 'box' },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item name="new_shelf_life" label={t('receiving.newShelfLife')}>
                    <InputNumber min={1} max={3650} precision={0} />
                  </Form.Item>
                </Space>
                {candidates && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={t('receiving.similarTitle')}
                    description={
                      <>
                        <List
                          size="small"
                          dataSource={candidates}
                          renderItem={(c) => (
                            <List.Item
                              actions={[
                                <Button
                                  key="use"
                                  size="small"
                                  onClick={() => {
                                    setMode('existing');
                                    setProduct(c.product);
                                    form.setFieldValue('product_id', c.product.id);
                                    setCandidates(null);
                                  }}
                                >
                                  {t('receiving.useThis')}
                                </Button>,
                              ]}
                            >
                              {c.product.name} <Tag>{uomLabel(t, c.product.base_uom)}</Tag>
                            </List.Item>
                          )}
                        />
                        <Button
                          danger
                          loading={receive.isPending}
                          onClick={() => {
                            void form.validateFields().then((v) => {
                              submit(v, true);
                            });
                          }}
                        >
                          {t('receiving.createAnyway')}
                        </Button>
                      </>
                    }
                  />
                )}
              </>
            )}
            <Space size="large" wrap align="start">
              <Form.Item
                name="qty"
                label={t('receiving.qty')}
                rules={[qtyRule({ required: true, positive: true })]}
                extra={uom ? undefined : t('receiving.qtyNeedsProduct')}
              >
                <QtyInput uom={uom ?? 'kg'} disabled={uom === null} style={{ width: 180 }} />
              </Form.Item>
              {uom === 'box' && (
                <Form.Item
                  name="box_weight"
                  label={t('receiving.boxWeight')}
                  extra={t('receiving.boxWeightHint')}
                  rules={[qtyRule({ required: false, positive: true })]}
                >
                  <QtyInput uom="kg" style={{ width: 180 }} />
                </Form.Item>
              )}
              <Form.Item name="expires_on" label={t('receiving.expiresOn')} extra={expiryHint()}>
                <DatePicker format={DATE} style={{ width: 180 }} />
              </Form.Item>
            </Space>
            <Space size="large" wrap align="start">
              <Form.Item
                name="pallets"
                label={t('receiving.pallets')}
                extra={t(noPallets ? 'receiving.noPalletsHint' : 'receiving.palletsHint')}
                rules={[{ required: !noPallets, message: t('receiving.palletsRequired') }]}
              >
                <InputNumber min={1} max={999} precision={0} disabled={noPallets} />
              </Form.Item>
              <Form.Item name="no_pallets" valuePropName="checked" label=" ">
                <Checkbox
                  onChange={(e) => {
                    if (e.target.checked) form.setFieldValue('pallets', null);
                    void form.validateFields(['pallets']);
                  }}
                >
                  {t('receiving.noPallets')}
                </Checkbox>
              </Form.Item>
            </Space>
            <Space size="large" wrap>
              <Form.Item name="received_on" label={t('receiving.receivedOn')}>
                <DatePicker
                  format={DATE}
                  allowClear={false}
                  disabledDate={(d) => d.isAfter(dayjs(), 'day')}
                />
              </Form.Item>
              <Form.Item name="supplier" label={t('receiving.supplier')}>
                <Input maxLength={100} style={{ width: 220 }} />
              </Form.Item>
            </Space>
            <Space size="large" wrap>
              <Form.Item name="country" label={t('receiving.country')}>
                <Input maxLength={100} style={{ width: 180 }} />
              </Form.Item>
              <Form.Item name="caliber" label={t('receiving.caliber')}>
                <Input maxLength={100} style={{ width: 140 }} />
              </Form.Item>
            </Space>
            <Form.Item name="comment" label={t('receiving.comment')}>
              <Input.TextArea maxLength={2000} autoSize={{ minRows: 1, maxRows: 4 }} />
            </Form.Item>
            {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
            <Button
              type="primary"
              htmlType="submit"
              loading={receive.isPending}
              disabled={candidates !== null}
              // Телефон: главная кнопка экрана — крупная, во всю ширину (T-176).
              {...(formMobile ? { size: 'large' as const, block: true } : {})}
            >
              {t('receiving.submit')}
            </Button>
          </Form>
        </Card>
      </Col>
      <Col xs={24} xl={10}>
        <SessionCard
          queued={outbox.queued.filter((o) => o.kind === 'receive')}
          batches={session}
          onNewSupply={() => {
            setSession([]);
            form.resetFields();
            setMode('existing');
            setProduct(null);
          }}
        />
      </Col>
    </Row>
  );
}

function SessionCard({
  batches,
  queued,
  onNewSupply,
}: {
  batches: BatchOut[];
  queued: OutboxOp[];
  onNewSupply: () => void;
}) {
  const { t } = useTranslation();
  const [printing, setPrinting] = useState<LabelTarget | null>(null);
  const mobile = useIsMobile();
  // Телефон (T-176): партия одной ячейкой, «Этикетки» — рядом.
  const mobileColumns: TableColumnsType<BatchOut> = [
    {
      key: 'batch',
      title: t('receiving.colBatch'),
      render: (_: unknown, b) => (
        <Space direction="vertical" size={2}>
          <Typography.Text strong>
            №{b.id} · {b.product.name}
          </Typography.Text>
          <Space size={4} wrap>
            <QtyCell value={b.qty_received} uom={b.product.base_uom} />
            {b.pallets === null ? (
              <Tag>{t('receiving.noPalletsShort')}</Tag>
            ) : (
              <Tag>
                {t('receiving.colPallets')}: {b.pallets}
              </Tag>
            )}
            {b.expires_on ? dayjs(b.expires_on).format(DATE) : <Tag>{t('receiving.noExpiry')}</Tag>}
          </Space>
          {b.pallets !== null && (
            <Button
              size="small"
              onClick={() => {
                setPrinting({ id: b.id, pallets: b.pallets ?? 0, product: b.product.name });
              }}
            >
              {t('labels.short')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const columns: TableColumnsType<BatchOut> = [
    { key: 'id', title: t('receiving.colBatch'), dataIndex: 'id', width: 80 },
    { key: 'product', title: t('receiving.product'), render: (_: unknown, b) => b.product.name },
    {
      key: 'qty',
      title: t('receiving.colQty'),
      render: (_: unknown, b) => <QtyCell value={b.qty_received} uom={b.product.base_uom} />,
    },
    {
      key: 'pallets',
      title: t('receiving.colPallets'),
      render: (_: unknown, b) => b.pallets ?? <Tag>{t('receiving.noPalletsShort')}</Tag>,
    },
    {
      key: 'expires',
      title: t('receiving.colExpires'),
      render: (_: unknown, b) =>
        b.expires_on ? (
          <Space size={4}>
            {dayjs(b.expires_on).format(DATE)}
            {b.expiry_source && (
              <Typography.Text type="secondary">
                ({t(`receiving.expirySource.${b.expiry_source}`)})
              </Typography.Text>
            )}
          </Space>
        ) : (
          <Tag>{t('receiving.noExpiry')}</Tag>
        ),
    },
    {
      key: 'labels',
      render: (_: unknown, b) =>
        b.pallets !== null && (
          <Button
            size="small"
            onClick={() => {
              setPrinting({ id: b.id, pallets: b.pallets ?? 0, product: b.product.name });
            }}
          >
            {t('labels.short')}
          </Button>
        ),
    },
  ];
  return (
    <Card
      title={t('receiving.sessionTitle')}
      extra={
        batches.length > 0 && (
          <Button size="small" onClick={onNewSupply}>
            {t('receiving.newSupply')}
          </Button>
        )
      }
    >
      {queued.length > 0 && (
        <List
          size="small"
          style={{ marginBottom: 12 }}
          header={<Typography.Text strong>{t('offline.inQueue')}</Typography.Text>}
          dataSource={queued}
          renderItem={(op) => (
            <List.Item>
              <Space>
                <Tag color="gold">{t('offline.waiting')}</Tag>
                {op.label}
              </Space>
            </List.Item>
          )}
        />
      )}
      <Table<BatchOut>
        rowKey="id"
        size="small"
        columns={mobile ? mobileColumns : columns}
        dataSource={batches}
        pagination={false}
        locale={{ emptyText: t('receiving.sessionEmpty') }}
      />
      {printing && (
        <LabelPrintModal
          batch={printing}
          onClose={() => {
            setPrinting(null);
          }}
        />
      )}
    </Card>
  );
}
